<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import { SEARCH_METER_CODE } from '../utils/constants';
import router from '@/router';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';
import ProgressFrame from './layout/ProgressFrame.vue';
import VisualFailure from '../components/VisualFailure.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import { optimisticallyGetCommissionState } from '../utils/output-connections';
import { useNpsStore } from '../stores/npsStore';

const commissioningDeviceStore = useCommissioningDeviceStore();
const deviceSpecificationStore = useDeviceSpecificationStore();
const npsStore = useNpsStore();
const route = useRoute();
const serialNumber = route.params.serialNumber as string;
const isLoading = ref(true);
const errorMessage = ref('');

const retry = () => {
  router.replace({ name: 'SerialNumberHow' });
};

onMounted(async () => {
  // Capture before findSolShare $reset() clears recommissionPrepare.
  const fromRecommission = commissioningDeviceStore.recommissionPrepare.status === 'success';

  const searchResultCode = await commissioningDeviceStore.findSolShare(serialNumber);

  switch (searchResultCode) {
    case SEARCH_METER_CODE.READY_TO_GO:
    case SEARCH_METER_CODE.NOT_COMMISSIONED: {
      if (!commissioningDeviceStore.softwareUpdate.needSoftwareUpdate) {
        optimisticallyGetCommissionState();
      }
      await getSolshareConfigFromBackend();
      await router.replace({ name: 'SearchingSolshareFound' });
      npsStore.resetNetPromoterScore();
      break;
    }
    case SEARCH_METER_CODE.COMMISSIONING:
      await router.replace({ name: 'CommissionInProgress' });
      break;
    case SEARCH_METER_CODE.NEED_POWER_CYCLE:
      await router.replace({ name: 'SearchingSolshareNeedPowerCycle' });
      break;
    case SEARCH_METER_CODE.OFFLINE:
      await router.replace({ name: 'SearchingSolshareOffline' });
      break;
    case SEARCH_METER_CODE.ALREADY_COMMISSIONED:
      if (fromRecommission) {
        // Prepare finished but Postgres still shows commissioned — treat as prepare failure.
        commissioningDeviceStore.updateRecommissionPrepare({ status: 'failed' });
        await router.replace({ name: 'SearchingSolshareRecommissionPrepareFailed' });
      } else {
        await router.replace({ name: 'SearchingSolshareAlreadyCom' });
      }
      break;
    case SEARCH_METER_CODE.NOT_FOUND:
      errorMessage.value = 'We cannot find the SolShare you entered.';
      break;
    case SEARCH_METER_CODE.FORBIDDEN:
      errorMessage.value = 'You do not have permission to access this SolShare.';
      break;
    default:
      errorMessage.value = 'Something went wrong.';
  }

  isLoading.value = false;
});

const getSolshareConfigFromBackend = async () => {
  try {
    await deviceSpecificationStore.refreshSolshareConfigFromBackend(serialNumber);
  } catch (err) {
    console.log('Failed to restore SolShare config', err);
  }
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page" backgroundColor="bg-white">
    <div class="content">
      <template v-if="isLoading">
        <LoadingSpinner class="loading-spinner" size="5rem" />
        <h2>Searching...</h2>
        <p>We’re looking to see if we can find your SolShare online.</p>
      </template>

      <template v-else>
        <VisualFailure>
          <template #title> {{ errorMessage }} </template>
        </VisualFailure>

        <div class="action-container">
          <p>
            Please re-enter the serial number.
            <span class="error-message-note">
              If the problem persists, please contact Allume.
            </span>
          </p>

          <ButtonPrimary block @click="retry" data-tn="retry-btn"> Let's try again </ButtonPrimary>
        </div>
      </template>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.loading-spinner {
  margin-bottom: 2rem;
}

.error-message-note {
  display: block;
  margin-top: 0.5rem;
}

.action-container {
  width: 100%;
}
</style>
